import type React from 'react';
import {useState} from 'react';
import {Alert, Box, Button, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import {calloutText, capFloor, dayOf, monthOf, signedPct, STATUS_LABEL} from '../../domain/monitor';
import {failed} from '../../hooks/notices';
import {useMonitorActions, useMonitorQuery} from '../../hooks/useMonitor';
import type {PriceClause} from '../../types/monitorTypes';
import {ProfileSection} from '../components/ProfileSection';
import {RowAction} from '../components/RowAction';
import {ClauseDialog} from './ClauseDialog';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';

type Props = {databagId: number; path: string; openContracts: () => void};

/**
 * Contract-linked price monitor (spec 4.4): the index clauses of the category's contracts, what the index supports
 * at each next revision, and callouts for announced increases above it, revisions due and drivers down.
 */
export const PriceMonitorSection: React.FC<Props> = ({databagId, path, openContracts}) => {
    const {data, isLoading, isError} = useMonitorQuery(databagId, path);
    const actions = useMonitorActions(databagId, path);
    const [editing, setEditing] = useState<PriceClause | 'new' | null>(null);

    if (isError) return <ProfileSection title="Contract price monitor" help={OVERVIEW_HELP.priceMonitor}><Alert severity="error">Could not load the price monitor.</Alert></ProfileSection>;
    if (isLoading || !data) return <Skeleton variant="rounded" height={140}/>;

    const {clauses, callouts, options, canEdit} = data;
    const add = canEdit && options.contracts.length > 0 && (
        <Button size="small" startIcon={<Add/>} onClick={() => setEditing('new')}>Add clause</Button>
    );

    return (
        <ProfileSection title="Contract price monitor" help={OVERVIEW_HELP.priceMonitor} subtitle={clauses.length ? `${clauses.length} index-linked clause${clauses.length === 1 ? '' : 's'}` : undefined}
                        actions={add || undefined}>
            {options.contracts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No contracts are linked to this category yet. Contracts appear here once Contract Intelligence links them
                    to its spend.
                </Typography>
            ) : clauses.length === 0 ? (
                <Stack gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        {`${options.contracts.length} contract${options.contracts.length === 1 ? '' : 's'} linked, no index clause entered. `}
                        Add a contract's price adjustment clause to see what its index supports at the next revision.
                    </Typography>
                    <Box><Button size="small" onClick={openContracts}>Open Contracts</Button></Box>
                </Stack>
            ) : (
                <Stack gap={1.5}>
                    {callouts.map(c => {
                        const {severity, text} = calloutText(c);
                        return <Alert key={`${c.kind}-${text}`} severity={severity} sx={{py: 0}}>{text}</Alert>;
                    })}
                    <Box sx={{overflowX: 'auto'}}>
                        <Table size="small" aria-label="Index-linked contract clauses" sx={{minWidth: 900}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Supplier</TableCell>
                                    <TableCell>Index reference</TableCell>
                                    <TableCell>Revision</TableCell>
                                    <TableCell>Cap / floor</TableCell>
                                    <TableCell>Trigger</TableCell>
                                    <TableCell>Next window</TableCell>
                                    <TableCell align="right">Index since base</TableCell>
                                    <TableCell align="right">Supported change</TableCell>
                                    <TableCell align="right">Announced</TableCell>
                                    {canEdit && <TableCell/>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clauses.map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{fontWeight: 500}}>{c.contract.supplier}</Typography>
                                            <Typography variant="caption" color="text.secondary">{c.contract.title}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{c.series.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {`${c.sharePct}% indexed · base ${monthOf(c.baseMonth)}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{options.revisions.find(r => r.value === c.revision)?.label ?? c.revision}</TableCell>
                                        <TableCell>{capFloor(c)}</TableCell>
                                        <TableCell>{c.triggerPct === null ? 'None' : `±${c.triggerPct}%`}</TableCell>
                                        <TableCell>{dayOf(c.window)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={c.latestMonth ? `Latest index month: ${monthOf(c.latestMonth)}` : 'No index data yet'}>
                                                <span>{signedPct(c.indexMovementPct)}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack alignItems="flex-end">
                                                <Typography variant="body2" sx={{fontWeight: 600}}>{signedPct(c.impliedPct)}</Typography>
                                                <Tag label={STATUS_LABEL[c.status]} solid={false}
                                                     color={c.status === 'capped' || c.status === 'floored' ? 'warning' : 'default'}/>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color={c.announcedPct !== null && c.impliedPct !== null
                                                && c.announcedPct > c.impliedPct + 0.5 ? 'error.main' : 'text.primary'}>
                                                {signedPct(c.announcedPct)}
                                            </Typography>
                                        </TableCell>
                                        {canEdit && (
                                            <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                                <RowAction kind="edit" label="clause" onClick={() => setEditing(c)}/>
                                                <RowAction kind="delete" label="clause" onClick={() => void actions.remove(c).catch(failed)}/>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Supported change: the index movement since the base month, times the indexed share, within the
                        trigger, cap and floor. Clause terms are entered by the category team from the contract.
                    </Typography>
                </Stack>
            )}
            {editing && (
                <ClauseDialog options={options} clause={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
                              onSubmit={input => editing === 'new' ? actions.add(input) : actions.update(editing, input)}/>
            )}
        </ProfileSection>
    );
};
